import { TRoles } from '../../../../shared/const'

// Als „kürzlich angemeldet" gilt, wer sich in den letzten 30 Tagen angemeldet
// hat. Der Wert steht hier und nicht in der Ansicht, damit Kachel und Prüfung
// dieselbe Grenze verwenden.
export const RECENT_LOGIN_DAYS = 30

const DAY = 24 * 60 * 60 * 1000

// Schlüssel für die Zeile „ohne Bundesland" in den Balken. Ein Zugang ohne
// Bundesland trägt dort `null`; als Filterwert wäre `null` nicht von „kein
// Filter" zu unterscheiden, deshalb dieser eigene Wert. Er kommt in TStates
// nicht vor und kann mit keinem echten Bundesland verwechselt werden.
export const WITHOUT_STATE = 'WITHOUT_STATE'

/**
 * Wendet die Auswahl aus den Balken auf die Liste an.
 *
 * Gefiltert wird im Browser und nicht über die Schnittstelle: Die Liste ist
 * ohnehin schon vollständig geladen (siehe LIMIT in index.jsx), so wirkt der
 * Klick ohne Wartezeit und ohne zweite Zählweise.
 *
 * Rolle und Bundesland gelten zusammen. Bundesadmin + ein Bundesland ergibt
 * regelmäßig eine leere Liste — das ist keine Panne, sondern die Aussage:
 * Bundesadmins sind österreichweit zuständig.
 */
export const filterAdmins = (admins, filter = {}) => {
	const list = Array.isArray(admins) ? admins : []

	return list.filter(admin => {
		if (filter.role && admin.role !== filter.role) return false
		if (filter.state === WITHOUT_STATE) return !admin.state
		if (filter.state && admin.state !== filter.state) return false
		return true
	})
}

/**
 * Rechnet die Übersicht über der Administratoren-Liste aus: wie viele es gibt,
 * wie sie sich auf die beiden Rollen verteilen und wie viele je Bundesland
 * zuständig sind.
 *
 * Bewusst ohne React und ohne Übersetzung — die Ansicht setzt nur noch
 * Beschriftungen davor. So lässt sich die Rechnung für sich prüfen.
 *
 * @param admins  Liste wie sie die Schnittstelle liefert
 * @param states  Bundesland-Schlüssel in der gewünschten Reihenfolge; jedes
 *                davon erscheint im Ergebnis, auch mit Null. Ein Bundesland
 *                ohne Landesadmin ist die eigentlich interessante Aussage.
 */
export const buildAdminStats = (admins, states = [], now = new Date()) => {
	const list = Array.isArray(admins) ? admins : []
	const recentSince = now.getTime() - RECENT_LOGIN_DAYS * DAY

	const counted = new Map(states.map(state => [state, 0]))
	let withoutState = 0

	list.forEach(admin => {
		if (!admin.state) {
			withoutState += 1
			return
		}
		// Ein Bundesland, das nicht in der Liste steht (alter Datenbestand),
		// zählt trotzdem mit — sonst stimmt die Summe der Balken nicht.
		counted.set(admin.state, (counted.get(admin.state) || 0) + 1)
	})

	const byState = Array.from(counted, ([state, count]) => ({ state, count }))

	const alumni = list.filter(admin => admin.role === TRoles.ALUMNI).length

	const loginTimes = list.map(admin => (admin.lastLoginAt ? new Date(admin.lastLoginAt).getTime() : null))

	return {
		total: list.length,
		byRole: [
			{ role: TRoles.FEDERAL_ADMIN, count: list.filter(admin => admin.role === TRoles.FEDERAL_ADMIN).length },
			{ role: TRoles.ADMIN, count: list.filter(admin => admin.role === TRoles.ADMIN).length },
			// Alumni (#213, Nur-Lese-Zugang) nur, wenn es welche gibt — eine
			// Kachel „0 Alumni" wäre bei den meisten Ständen bloß Rauschen.
			...(alumni ? [{ role: TRoles.ALUMNI, count: alumni }] : [])
		],
		byState,
		// Zugänge ohne Bundesland (in aller Regel die Bundesadmins). Sie stehen
		// als eigene Zeile unter den Balken — sonst ergäbe die Summe der Balken
		// weniger als die Gesamtzahl.
		withoutState,
		// Größter Balken — die Balkenlänge ist ein Anteil davon, nicht am
		// Gesamtbestand: Sonst bliebe bei 18 Zugängen jeder Balken ein Strich.
		// Die Zeile „ohne Bundesland" zählt mit, sie wird im selben Raster
		// gezeichnet und liefe sonst über den Rand hinaus.
		maxState: byState.reduce((acc, entry) => Math.max(acc, entry.count), withoutState),
		recentLogins: loginTimes.filter(time => time !== null && time >= recentSince).length,
		withoutLogin: loginTimes.filter(time => time === null).length
	}
}
